/**
 * library-hub/tokens.ts — extended design token vocabulary for
 * the Library Hub components.
 *
 * Re-exports the workspace-level palette from ../tokens so every
 * hub component only needs to import from here. On top of the
 * shared palette, adds:
 *
 *   spacing    — consistent 4px-grid values (no magic numbers)
 *   radius     — border-radius scale
 *   elevation  — box-shadow levels (none / sm / md / lg)
 *   transition — named CSS transition strings
 *   statusLane — 2px left-border colours keyed by ProcessingStatus
 */

// Re-export shared palette so hub components can import everything
// from a single path.
export {
  brandColors,
  fonts,
  inkColors,
  statusColors,
  surfaceColors,
} from "../tokens";

// ─── Spacing ──────────────────────────────────────────────────────────────────
// 4 px grid. Suffix is the multiplier (sp1 = 4px, sp2 = 8px …).
export const spacing = {
  sp0: "2px",
  sp1: "4px",
  sp2: "8px",
  sp3: "12px",
  sp4: "16px",
  sp5: "20px",
  sp6: "24px",
  sp8: "32px",
} as const;

// ─── Radius ───────────────────────────────────────────────────────────────────
export const radius = {
  none: "0",
  xs: "2px",
  sm: "3px",
  md: "4px",
  lg: "6px",
  xl: "8px",
  full: "9999px",
} as const;

// ─── Elevation ────────────────────────────────────────────────────────────────
// Three levels of box-shadow matching the Linear / Notion surface
// hierarchy.  ``none`` keeps docs coherent ("no shadow" is still
// an explicit choice, not an omission).
export const elevation = {
  none: "none",
  sm: "0 1px 3px rgba(15, 10, 30, 0.08), 0 1px 2px rgba(15, 10, 30, 0.06)",
  md: "0 4px 12px rgba(15, 10, 30, 0.10), 0 2px 4px rgba(15, 10, 30, 0.06)",
  lg: "0 8px 24px rgba(15, 10, 30, 0.14), 0 2px 8px rgba(15, 10, 30, 0.08)",
} as const;

// ─── Transition ───────────────────────────────────────────────────────────────
// Named CSS transition strings. Import the literal value and drop
// it directly into a makeStyles property.
export const transition = {
  /** Micro-interaction: hover highlights, pill colour swaps. */
  fast: "all 80ms ease",
  /** Standard interactive feedback: button state, filter toggles. */
  base: "all 150ms ease",
  /** Panel entry / content-area slides. */
  panel: "all 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
} as const;

// ─── Status lane colours ──────────────────────────────────────────────────────
// Keyed by ProcessingStatus values from library-hub/types.ts.
// Used as the 2 px left-border stripe on DocRow to give each row a
// at-a-glance lifecycle state without overwhelming the text content.
//
// Anything not explicitly listed (e.g. unexpected server values)
// resolves to transparent so the lane disappears gracefully.
export const statusLane: Record<string, string> = {
  uploading: "#6366F1", // indigo — in-flight upload
  indexing: "#8B5CF6", // violet — embedding / indexing
  classifying: "#8B5CF6", // violet — LLM classification pass
  ready: "#10B981", // emerald — processing complete
  limited: "#F59E0B", // amber — processed but degraded
  failed_upload: "#EF4444", // red — terminal failure (upload)
  failed_processing: "#EF4444", // red — terminal failure (proc)
};

// ─── AI-native semantic colors ────────────────────────────────────────────────
// Colors that communicate AI lifecycle states — "understanding",
// "classified", "verified", "uncertain". Used in Phase 2 AI-native
// UX components only.
export const aiColors = {
  // AI activity: active LLM processing — indigo-violet spectrum.
  thinking: "#6366F1", // indigo-500 — ingesting / indexing
  classifying: "#8B5CF6", // violet-500 — LLM classification pass

  // Evidence quality: maps 1:1 to confidence tiers.
  evidenceHigh: "#10B981", // emerald-500 — conf ≥ 0.85
  evidenceMid: "#F59E0B", // amber-500  — conf 0.60–0.84
  evidenceLow: "#EF4444", // red-500    — conf < 0.60

  // Trust: document sign-off states.
  trustVerified: "#059669", // emerald-600 — writer has signed off
  trustPending: "#D97706", // amber-600  — needs attention

  // AI annotation surface: background + border for AI-generated
  // content blocks (rationale, classification intel panel).
  annotationBg: "#F8F7FF", // violet-50 tint
  annotationBorder: "#DDD6FE", // violet-200

  // "Understood" — ready, classified, usable as evidence.
  understood: "#0D9488", // teal-600
} as const;
